import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { MultiOptionsFilterComponent } from 'features/insights/components/compare-bar/report-actions-bar/advance-filters-modal/multi-entities-filter/types';
import { FilterOperator } from '@placer-ui/types';
import {
    BasicDropdownMenu,
    BasicMenuItemProps,
    Dropdown,
    SingleSelect,
    UnifiedSelectBox,
    useTheme,
} from '@placer-ui/components';
import styles from './visit-duration-filter.module.scss';
import { getFeatureFlags, hasPermissions } from 'core/flow-control';
import { MORE_THEN_FILTER_OPERATOR } from 'core/constants/filter-attributes';
import { APP_CONSTS } from 'core/constants/app-consts';
import { useReportPlaces } from 'extensions/widget/hooks/use-poi-hooks';
import { getDisabledOptionTooltip, shouldDisableVisitDurationNumericOption, shouldDisableVisitDurationOperatorOption } from 'utils/daily-duration/daily-duration-filter';

const OPERATORS = APP_CONSTS.filter.operators;
const LIMITED_SHORT_VALUE = 10;

export const VISITS_DURATION_REGULAR_OPTIONS = [5, 10, 15, 30, 45, 60, 75, 90, 105, 120, 135, 150];
const VISITS_DURATION_EXTENDED_OPTIONS = [180, 210, 240, 300, 360];

const getMinValidValue = (minThreshold: number, visitDurationOptions: BasicMenuItemProps[]) => {
    const validOption = visitDurationOptions.find(
        (option) => Number(option.value) > minThreshold,
    );
    return validOption?.value;
};

// TODO: Fix shenanigans with zIndex - PLAC-67966
// appendToParent is not working as expected with tooltips,
// as we tooltip is cutoff by the parent element
export const VisitDurationFilter: MultiOptionsFilterComponent<number> = ({
    filter,
    onChange,
    entity,
    mode,
    ariaLabel,
}) => {
    const { enable_legacy_builders_for_regions_ff, accessibility_advance_filters_modal_ff } =
        getFeatureFlags();
    const { all } = useReportPlaces();
    const { zIndexes } = useTheme();
    const isPosieEntity = entity?.isPosie || !!all?.[0]?.originalEntity?.isPosie;
    const entityMinVisitDuration = entity?.minVisitDuration || all?.[0]?.originalEntity?.minVisitDuration || 0;
    const triggerRef = useRef<HTMLDivElement>(null);

    const isLimitedVisitDuration = useMemo(() => {
        const isLimitedEntity = entity?.isLimitedShortVisits;
        const isAllModeAndHasLimited =
            mode === 'all' &&
            all.some(({ originalEntity }) => originalEntity?.isLimitedShortVisits);

        return enable_legacy_builders_for_regions_ff && (isLimitedEntity || isAllModeAndHasLimited);
    }, [all, enable_legacy_builders_for_regions_ff, entity?.isLimitedShortVisits, mode]);

    const onVisitDurationChange = useCallback(
        ({ value }: BasicMenuItemProps<string>) => {
            onChange({
                operator: filter?.operator as FilterOperator,
                value: Number(value),
            });
        },
        [onChange, filter?.operator],
    );

    const visitDurationOptions: BasicMenuItemProps[] = useMemo(() => {
        let options = [...VISITS_DURATION_REGULAR_OPTIONS];

        if (hasPermissions(['longer_durations_length_of_stay_filter'])) {
            options = [...options, ...VISITS_DURATION_EXTENDED_OPTIONS];
        }

        return options.map((option) => {
            const isLimitedDisabled = isLimitedVisitDuration && option <= LIMITED_SHORT_VALUE;
            const isDailyDurationDisabled = shouldDisableVisitDurationNumericOption(option, filter?.operator as FilterOperator, isPosieEntity, entityMinVisitDuration);
            const isDisabled = isLimitedDisabled || isDailyDurationDisabled;

            let tooltip: string | undefined;
            if (isDailyDurationDisabled) {
                tooltip = getDisabledOptionTooltip();
            }

            return {
                label: `${option} min`,
                value: option.toString(),
                isSelected: Number(filter?.value) === option,
                multi: 'checkbox',
                isDisabled,
                appendToParent: true,
                ...(tooltip ? {
                    tooltip: {
                        title: tooltip,
                        zIndex: zIndexes.modal + 1,
                    },
                } : {}),
            };
        });
    }, [entityMinVisitDuration, filter?.value, filter?.operator, isLimitedVisitDuration, isPosieEntity, zIndexes.modal]);

    const operatorOptions = useMemo(() => {
        const filterValue = filter?.value;
        const filterOperator = filter?.operator;

        return [
            {
                label: 'Any',
                value: 'any',
                isSelected: filterValue === 0,
                isDisabled: shouldDisableVisitDurationOperatorOption('any', isPosieEntity, entityMinVisitDuration),
                ...(shouldDisableVisitDurationOperatorOption('any', isPosieEntity, entityMinVisitDuration) ? {
                    tooltip: {
                        title: getDisabledOptionTooltip(),
                        zIndex: zIndexes.modal + 1,
                    },
                } : {}),
            },
            {
                label: 'Less than',
                value: OPERATORS.LESS_THEN,
                isSelected: filterOperator === OPERATORS.LESS_THEN,
            },
            {
                label: 'More than',
                value: OPERATORS.MORE_THEN_OR_EQUAL,
                isSelected:
                    !!filterValue &&
                    filterValue !== 0 &&
                    filterOperator === OPERATORS.MORE_THEN_OR_EQUAL,
            },
        ];
    }, [entityMinVisitDuration, filter?.operator, filter?.value, isPosieEntity, zIndexes.modal]);

    const onOperatorChange = useCallback(
        ({ value }: BasicMenuItemProps<string>) => {
            if (shouldDisableVisitDurationOperatorOption(value as FilterOperator, isPosieEntity, entityMinVisitDuration)) {
                return;
            }

            triggerRef.current?.focus();
            if (value === 'any') {
                onChange({
                    operator: MORE_THEN_FILTER_OPERATOR,
                    value: 0,
                });
            } else {
                let filterValue;
                if (filter?.value === 0) {
                    filterValue = visitDurationOptions[visitDurationOptions.length - 1].value;
                } else {
                    filterValue = filter?.value ?? visitDurationOptions[0].value;
                }
                if (shouldDisableVisitDurationNumericOption(Number(filterValue), value as FilterOperator, isPosieEntity, entityMinVisitDuration)) {
                    filterValue = getMinValidValue(Number(filterValue), visitDurationOptions);
                }

                onChange({
                    operator: value as FilterOperator,
                    value: Number(filterValue),
                });
            }
        },
        [onChange, filter?.value, visitDurationOptions, isPosieEntity, entityMinVisitDuration],
    );

    useEffect(() => {
        if (!(enable_legacy_builders_for_regions_ff && isLimitedVisitDuration)) return;

        const value = filter?.value || 0;
        const isLimitedAlreadySelected =
            filter?.operator === MORE_THEN_FILTER_OPERATOR && value === LIMITED_SHORT_VALUE;

        if (mode === 'all' && !isLimitedAlreadySelected && value < LIMITED_SHORT_VALUE) {
            onChange({
                operator: MORE_THEN_FILTER_OPERATOR,
                value: 10,
            });
        }
    }, [
        enable_legacy_builders_for_regions_ff,
        filter?.operator,
        filter?.value,
        isLimitedVisitDuration,
        mode,
        onChange,
    ]);

    return (
        <div className={styles.container}>
            <div className={styles.filterContainer}>
                {accessibility_advance_filters_modal_ff ? (
                    <Dropdown
                        closeOnSelection
                        trigger={
                            <UnifiedSelectBox
                                ref={triggerRef}
                                aria-label={`${ariaLabel} start range`}
                                testId="length-of-stay-filter-operator"
                                value={operatorOptions.find((o) => o.isSelected)?.label}
                                disabled={isLimitedVisitDuration}
                            />
                        }
                        menuWidth={MENU_WIDTH}
                        appendToParent
                    >
                        <BasicDropdownMenu
                            items={operatorOptions}
                            onItemSelection={onOperatorChange}
                        />
                    </Dropdown>
                ) : (
                    <SingleSelect
                        align="end"
                        side="bottom"
                        testId="length-of-stay-filter-operator"
                        items={operatorOptions}
                        maxVisibleItems={5}
                        onItemSelection={onOperatorChange}
                        disabled={isLimitedVisitDuration}
                        appendToParent
                    />
                )}
            </div>
            <div className={styles.filterContainer}>
                {accessibility_advance_filters_modal_ff ? (
                    <Dropdown
                        closeOnSelection
                        trigger={
                            <UnifiedSelectBox
                                aria-label={`${ariaLabel} end range`}
                                testId="length-of-stay-filter"
                                value={
                                    visitDurationOptions.find((o) => o.isSelected)?.label as string
                                }
                                disabled={!filter?.operator}
                            />
                        }
                        menuWidth={MENU_WIDTH}
                        appendToParent
                    >
                        <BasicDropdownMenu
                            items={visitDurationOptions}
                            onItemSelection={onVisitDurationChange}
                        />
                    </Dropdown>
                ) : (
                    <SingleSelect
                        align="start"
                        side="bottom"
                        size="small"
                        testId="length-of-stay-filter"
                        items={visitDurationOptions}
                        disabled={!filter?.operator} // do not allow selecting visit duration until an operator is selected
                        maxVisibleItems={5}
                        onItemSelection={onVisitDurationChange}
                        appendToParent
                    />
                )}
            </div>
        </div>
    );
};

const MENU_WIDTH = 160;
